import { LitElement, html } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import type { SlDialog } from '@shoelace-style/shoelace';

// Shoelace component imports
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

import { styles } from './password-change-request-styles.js';
import { ApiFactory } from '../../api/agwasuite-api-factory.js';
import { ApiLogin } from '../../api/core/agwasuite-api-login.js';

enum PasswordChangeRequestState {
  Confirmation,
  Success,
  Error,
}

@customElement('password-change-request-dialog')
export class PasswordChangeRequestDialog extends LitElement {
  static styles = [styles];

  @state()
  private dialogState: PasswordChangeRequestState =
    PasswordChangeRequestState.Confirmation;

  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';

  @query('#password-change-dialog')
  private dialog!: SlDialog;

  /**
   * Opens the password change request dialog
   */
  public open() {
    this.dialogState = PasswordChangeRequestState.Confirmation;
    this.isLoading = false;
    this.errorMessage = '';
    void this.dialog.show();
  }

  /**
   * Closes the dialog
   */
  public close() {
    void this.dialog.hide();
  }

  private async requestPasswordChange() {
    this.isLoading = true;

    try {
      const api = ApiFactory.authenticated(ApiLogin);
      const result = await api.passwordChangeRequired();

      if (result.success) {
        this.dialogState = PasswordChangeRequestState.Success;
      } else {
        this.dialogState = PasswordChangeRequestState.Error;
        this.errorMessage = result.error || 'An unknown error occurred.';
      }
    } catch (error) {
      this.dialogState = PasswordChangeRequestState.Error;
      this.errorMessage =
        'Failed to connect to the server. Please try again later.';
      console.error('Password change request error:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private getConfirmationTemplate() {
    return html`
      <div class="password-change-container">
        <sl-icon name="exclamation-triangle" class="warning-icon"></sl-icon>
        <h3>Request Password Change</h3>
        <p>For security reasons, requesting a password change will:</p>
        <ul>
          <li>Log you out immediately after submission</li>
          <li>Require you to set a new password on your next login</li>
          <li>Invalidate your current password</li>
        </ul>
        <p class="security-note">
          This process helps ensure the security of your account. You will need
          to complete the password change process before accessing your account
          again.
        </p>
      </div>

      <div slot="footer">
        <sl-button
          variant="neutral"
          @click=${this.close}
          ?disabled=${this.isLoading}
        >
          Cancel
        </sl-button>
        <sl-button
          variant="primary"
          @click=${this.requestPasswordChange}
          ?disabled=${this.isLoading}
        >
          ${
            this.isLoading
              ? html`<sl-spinner></sl-spinner>`
              : 'Request Password Change'
          }
        </sl-button>
      </div>
    `;
  }

  private getSuccessTemplate() {
    return html`
      <div class="password-change-container success">
        <sl-icon name="check-circle-fill" class="success-icon"></sl-icon>
        <h3>Password Change Requested</h3>
        <p>
          Your password change request has been processed successfully. You will
          be logged out momentarily.
        </p>
        <p>On your next login, you will be prompted to set a new password.</p>
      </div>

      <div slot="footer">
        <sl-button variant="primary" @click=${this.logout}>
          Log Out Now
        </sl-button>
      </div>
    `;
  }

  private getErrorTemplate() {
    return html`
      <div class="password-change-container error">
        <sl-icon name="exclamation-octagon" class="error-icon"></sl-icon>
        <h3>Error</h3>
        <p>
          We encountered a problem while processing your password change
          request:
        </p>
        <sl-alert variant="danger" open> ${this.errorMessage} </sl-alert>
        <p>
          Please try again later or contact support if the problem persists.
        </p>
      </div>

      <div slot="footer">
        <sl-button variant="neutral" @click=${this.close}> Close </sl-button>
        <sl-button
          variant="primary"
          @click=${() =>
            (this.dialogState = PasswordChangeRequestState.Confirmation)}
        >
          Try Again
        </sl-button>
      </div>
    `;
  }

  private logout() {
    // Dispatch a custom event that app-profile will listen for
    this.dispatchEvent(
      new CustomEvent('logout', { bubbles: true, composed: true })
    );
    this.close();
  }

  render() {
    let dialogTitle;
    let contentTemplate;

    switch (this.dialogState) {
      case PasswordChangeRequestState.Confirmation:
        dialogTitle = 'Confirm Password Change Request';
        contentTemplate = this.getConfirmationTemplate();
        break;
      case PasswordChangeRequestState.Success:
        dialogTitle = 'Success';
        contentTemplate = this.getSuccessTemplate();
        break;
      case PasswordChangeRequestState.Error:
        dialogTitle = 'Error';
        contentTemplate = this.getErrorTemplate();
        break;
    }

    return html`
      <sl-dialog
        id="password-change-dialog"
        label="${dialogTitle}"
        @sl-request-close=${(e: CustomEvent) => {
          if (e.detail.source === 'overlay') e.preventDefault();
        }}
      >
        ${contentTemplate}
      </sl-dialog>
    `;
  }
}
